import windowNameKeys from '@/constants/window-name-keys';
import { windowService } from '@/services/jupiter/window-service';
import { siteService } from '@/services/site.service';
import { switchViewService } from '@/services/swtich-view.service';
import { useAppearanceStore } from '@/store/appearance';
import { getBetFrom } from '@/utils/common-util';
import { UserInfo } from '@nf/types/account';
import { LicenseeTypeEnums, LicSettingsData, SiteConfigsSettingsData } from '@nf/types/site';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { AccountActStatus } from '@nf/utils-common/constants';
import { useTheme } from 'next-themes';
import { useLocale } from 'next-intl';
import { isDesktop } from 'react-device-detect';
import { v4 as uuid } from 'uuid';
import { useAccountInfo } from '@/hooks/query/account';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { useLicSettings } from '@/domains/config/hooks/use-lic-settings';
import { TaskTypeEnum } from '@/domains/wc-promotion/types';
import { wcPromotionMissionService } from '@/domains/wc-promotion/services/wc-promotion-mission.service';

export interface CasinoRoyaleResponse {
	Seq: string;
	LaunchUrl: string;
	ErrorCode: number;
	ErrorMsg: string;
	TimeStamp: string;
}

const launchPortalInFlightMap = new Map<string, Promise<CasinoRoyaleResponse>>();

export type DeeplinkExtractInfo = {
	path?: string;
	sportTypeId?: number | string;
	betTypeId?: number | string;
	category?: number | string;
};

interface FetchCasinoRoyaleExecuteUrlParams {
	user?: UserInfo | null;
	siteConfig?: SiteConfigsSettingsData;
	isApp?: boolean;
	sportTypeId?: string | null;
	betTypeId?: string | null;
	category?: string | null;
	appType?: string | null;
	nonSport?: LicSettingsData['nonSport'];
	darkMode?: boolean;
	lang: string;
	platform?: string;
}

const fetchCasinoRoyaleExecuteUrl = async ({
	user,
	siteConfig,
	isApp,
	sportTypeId,
	betTypeId,
	category,
	appType,
	nonSport,
	darkMode,
	lang,
	platform,
}: FetchCasinoRoyaleExecuteUrlParams): Promise<CasinoRoyaleResponse> => {
	const isDemo = sportTypeId === '105' && user?.ActStatus !== AccountActStatus.Deposited;

	let productCode = '';
	if (sportTypeId === '172') {
		productCode = 'casinoRoyale';
	} else if (sportTypeId === '202') {
		productCode = 'keno';
	} else if (sportTypeId === '220') {
		productCode = 'kenoLottery';
	} else if (sportTypeId === '171') {
		productCode = 'vGaming';
	}
	if (productCode) {
		wcPromotionMissionService.notifyMission({
			taskType: TaskTypeEnum.ClickSabaFeatures,
			productCode
		});
	}
	const view = useAppearanceStore.getState().view;
	const themeColor = sportTypeId === '105' ? nonSport?.predictionMarketThemeColor : undefined;

	const now = new Date();

	const hostname = location.hostname === 'localhost'
		? siteService.getLicenseeType() === LicenseeTypeEnums.Api ? 'k957gu002.kl5888.com' : 'x999gu003.kl5888.com'
		: location.hostname;

	const returnUrl = !isApp ? undefined : `${location.protocol}//${hostname}`;

	const request = {
		TimeStamp: now.toISOString(),
		Seq: uuid(),
		TsId: user?.TsId,
		HomeUrl: sportTypeId === '105' ? returnUrl : undefined, // use when launching directly into a game
		CustInfo: {
			SiteId: siteConfig?.GetMessageID,
			Site: user?.AgentSite,
			ObCustId: user?.ID,
			LicenseeCustId: 0,
			ObCustName: user?.LoginUserName,
			Language: switchViewService.convertLanguageToShortCode(user?.Lang ?? lang, siteConfig!),
			CurrencyId: user?.CurrId,
			Currency: user?.Curr,
			BetFrom: getBetFrom(view, isDesktop),
			Platform: platform ?? (isDesktop ? 'd' : 'm'),
			Ip: user?.Ip,
			BrowserUserAgent: navigator.userAgent,
		},
		Additional: {
			Location: user?.FromCountry,
			Gallery: siteConfig?.ImageServerUrl,
			OneDomain: hostname,
			OddsServerURL: user?.OddsServerUrl,
			OperatorId: user?.OperatorId,
			EnableSingleWallet: siteConfig?.EnableSingleWallet,
			LicenseeApiUrl: siteConfig?.SingleWalletAdapterApi,
			SportTypeId: sportTypeId ?? undefined,
			BetTypeId: betTypeId ?? undefined,
			Category: category ?? undefined,
			AppType: appType ?? undefined,
			ExtendUrl: user?.LicenseeSiteSetting.ExtendSessionUrl,
			IsApp: isApp,
			ReturnUrl: sportTypeId === '105' ? undefined : returnUrl, // use for Royale portal navigation
			ThemeColor: themeColor ?? undefined,
			DarkMode: user?.DarkMode ?? darkMode,
			IsDemo: isDemo,
		}
	};
	if ((sportTypeId === '165' || sportTypeId === '172') && request.Additional.BetTypeId === '') {
		delete request.Additional.BetTypeId;
	}
	if ((sportTypeId === '165' || sportTypeId === '172') && request.Additional.Category === '') {
		delete request.Additional.Category;
	}
	if (sportTypeId === '132') { // GPI Lottery
		delete request.Additional.BetTypeId;
		delete request.Additional.Category;
	}
	if (sportTypeId === '119') { // OFA Live
		delete request.Additional.AppType;
		delete request.Additional.Category;
		request.Additional.BetTypeId = '0';
	}
	const options = {
		body: JSON.stringify(request)
	};

	const casinoRoyaleUrl = new URL('/api/Portal/GetLaunchPortalUrl', siteConfig?.SealComponentApiUrl);
	const requestKey = [
		casinoRoyaleUrl.href,
		user?.AccessToken ?? '',
		user?.ID ?? '',
		siteConfig?.GetMessageID ?? '',
		sportTypeId ?? '',
		betTypeId ?? '',
		category ?? '',
		appType ?? '',
		isApp ? '1' : '0'
	].join('|');
	const inFlightRequest = launchPortalInFlightMap.get(requestKey);
	if (inFlightRequest) {
		return inFlightRequest;
	}

	const requestPromise = fetchComposer.postWithBearer(casinoRoyaleUrl.href, options)(user?.AccessToken ?? '')
		.then(async response => {
			if (!response.ok) {
				throw new Error(`${response.status}: ${response.statusText}`);
			}
			return await response.json();
		})
		.finally(() => {
			launchPortalInFlightMap.delete(requestKey);
		});

	launchPortalInFlightMap.set(requestKey, requestPromise);
	return requestPromise;
}

const openCasinoRoyaleInNewWindow = (params: FetchCasinoRoyaleExecuteUrlParams) => {
	const { isApp, sportTypeId, betTypeId } = params;

	if (isApp) {
		fetchCasinoRoyaleExecuteUrl(params).then(response => {
			location.href = response.LaunchUrl;
		});
	} else {
		const windowName = `${windowNameKeys.CASINO}-${sportTypeId}-${betTypeId}`;
		const isAthena = siteService.getLicenseeType() === LicenseeTypeEnums.Athena;
		const features = isAthena ? 'toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=1024,height=768' : '';

		windowService.openWindow(windowName, 'about:blank', '_blank', features);

		fetchCasinoRoyaleExecuteUrl(params).then(response => {
			windowService.openWindow(windowName, response.LaunchUrl, '_blank', features);
		});
	}
}

export interface OpenCasinoRoyaleParams {
	sportTypeId?: string | null;
	betTypeId?: string | null;
	category?: string | null;
	appType?: string | null;
	isApp?: boolean;
	platform?: string;
}

export const useCasinoRoyale = () => {
	const { user } = useAccountInfo();
	const { SiteConfigsData } = useGetSiteConfigs();
	const { nonSport } = useLicSettings();
	const { theme } = useTheme();
	const locale = useLocale();

	const openCasinoRoyale = (params: OpenCasinoRoyaleParams = {}) => {
		const allowsBeforeUser = params.sportTypeId === '105';
		if ((user || allowsBeforeUser) && SiteConfigsData) {
			openCasinoRoyaleInNewWindow({
				user,
				siteConfig: SiteConfigsData,
				nonSport,
				darkMode: theme === 'dark',
				lang: locale,
				...params
			});
		}
	};

	const fetchCasinoRoyaleUrl = async (params: OpenCasinoRoyaleParams = {}): Promise<CasinoRoyaleResponse | null> => {
		const allowsBeforeUser = params.sportTypeId === '105';
		if ((user || allowsBeforeUser) && SiteConfigsData) {
			wcPromotionMissionService.notifyMission({
				taskType: TaskTypeEnum.ClickSabaFeatures,
				productCode: 'casinoRoyale'
			});
			return fetchCasinoRoyaleExecuteUrl({
				user,
				siteConfig: SiteConfigsData,
				nonSport,
				darkMode: theme === 'dark',
				lang: locale,
				...params
			});
		}
		return null;
	};

	return {
		openCasinoRoyale,
		fetchCasinoRoyaleUrl
	};
};